import React, { useEffect, useMemo, useRef, useState } from 'react';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from '@utils/dayjs';
import {
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Theme,
  Tooltip
} from '@mui/material';

import {
  ConfirmationModal,
  MDTypography,
  DataTable,
  MDBox,
  FileUploadModal,
  FullPageLoader,
  FaIcon
} from '@components/index';
import MainLayout from '@layouts/MainLayout';
import EmptyProjects from './components/EmptyProjects';
import LinearProgressIndicator from './LinearProgressIndicator';

import { useGetProjectsQuery } from '@api/projects';
import { setMiniSidenav, useMaterialUIController } from '@context/MDContext';

import { sendGAData } from '@utils/googleAnalytics';
import { sendAmplitudeData } from '@utils/amplitude';
import useDeleteProject from '@hooks/useDeleteProject';
import useToast from '@hooks/useToast';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { ProjectStatus } from '@constants/common';
import { MyProjectsPage } from '@constants/toolTips';
import { Colors } from '@constants/theme';
import {
  CLICKED_KNOW_MORE,
  DELETE_PROJECT,
  NEW_PROJECT,
  VISITED_MY_PROJECTS_PAGE
} from '@constants/eventNames';

export const getProjectStatus = (status: string) => {
  switch (status) {
    case ProjectStatus.READY:
    case ProjectStatus.REVIEWED:
      return {
        color: 'success',
        status: <>Ready</>,
        tooltipText: MyProjectsPage.READY
      };
    case ProjectStatus.INGESTED:
      return {
        color: 'secondary',
        status: <>Processed</>,
        tooltipText: MyProjectsPage.PROCESSED
      };
    case ProjectStatus.QUEUED:
    case ProjectStatus.PROCESSING:
      return {
        color: 'secondary',
        status: <>Processing</>,
        tooltipText: MyProjectsPage.PROCESSING
      };
    case ProjectStatus.CLEANING:
      return {
        color: 'warning',
        status: <>Cleaning</>,
        tooltipText: MyProjectsPage.CLEANING
      };
    case ProjectStatus.ADDING_DATA:
      return {
        color: 'secondary',
        status: <>Adding Data</>,
        tooltipText: MyProjectsPage.PROCESSING
      };
    case ProjectStatus.COMBINING:
      return {
        color: 'warning',
        status: <>Combining Data</>,
        tooltipText: MyProjectsPage.PROCESSING
      };
    case ProjectStatus.FORMAT_ERROR:
      return {
        color: 'error',
        status: <>Error In File</>,
        tooltipText: (
          <div style={{ textAlign: 'left' }}>
            Could not upload your file. Possible reasons:
            <br />
            <br /> - File is Password protected <br />- Missing Header row
            <br /> - Additional non-tabular info
            <br /> - Merged cells that DataSquirrel is unable to identify
            <br /> Please correct and re-upload. Refer to guidelines for upload
            format checks,{' '}
            <span
              role='presentation'
              onClick={() => sendAmplitudeData(CLICKED_KNOW_MORE)}
            >
              <Link
                href='https://www.notion.so/Want-to-know-more-about-Importing-data-into-DataSquirrel-ea284f2ef9f142939a2802ada6ff11b0'
                target='_blank'
                sx={{ borderBottom: '1px solid #fff' }}
              >
                here.
              </Link>
            </span>
          </div>
        )
      };
  }
};

const emptyArray: never[] = [];

const MyProjects: React.FC = () => {
  const { pushSuccessToast } = useToast();
  const [controller, dispatch] = useMaterialUIController();
  const { userModifiedSidenav } = controller;
  const navigate = useCustomNavigate();

  const { data, isSuccess, isLoading } = useGetProjectsQuery(null, {
    pollingInterval: 5000,
    selectFromResult: ({ data, isSuccess, isLoading, error }) => ({
      data: error?.originalStatus === 500 ? emptyArray : data,
      isSuccess,
      isLoading
    })
  });

  const { userDeleteProject } = useDeleteProject();

  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [selectedChartCount, setSelectedChartCount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [initLoad, setInitLoad] = useState(true);

  useEffect(() => {
    if (!userModifiedSidenav) setMiniSidenav(dispatch, false);
  }, []);

  useEffect(() => {
    if (initLoad && isSuccess) {
      sendAmplitudeData(VISITED_MY_PROJECTS_PAGE, {
        'Project Count': data?.length
      });

      setInitLoad(false);
    }
  }, [initLoad, isSuccess]);

  const MyProjectColumns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id',
        width: '5%',
        Cell: ({ cell }: any) => <>{Number(cell.row.id) + 1}</>
      },
      {
        Header: 'project name',
        accessor: 'project_name',
        width: '45%',
        Cell: ({ cell }: any) => {
          const typographyRef = useRef<HTMLDivElement>();

          const showTooltip =
            typographyRef?.current &&
            typographyRef?.current?.getBoundingClientRect().height > 19
              ? true
              : false;

          const isDisabled = ![
            ProjectStatus.READY,
            ProjectStatus.INGESTED,
            ProjectStatus.ADDING_DATA
          ].includes(cell.row.values.project_status);

          return (
            <Grid
              container
              display='flex'
              flexDirection='row'
              flexWrap='nowrap'
              alignItems='center'
            >
              <Grid item>
                <FaIcon
                  typographyProps={{ variant: 'lg', color: 'info' }}
                  icon={
                    cell.row.original.is_api_connected
                      ? 'gear'
                      : cell.row.original.project_datafiles?.[0]?.filetype ===
                        'CSV'
                      ? 'file-csv'
                      : cell.row.original.project_datafiles?.[0]?.filetype ===
                        'GSHEET'
                      ? faGoogle
                      : 'file-excel'
                  }
                />
              </Grid>
              <Grid item>
                <Tooltip
                  title={showTooltip ? cell.row.values.project_name || '' : ''}
                  placement='top'
                >
                  <MDBox
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      ml: 1,
                      cursor: isDisabled ? 'auto' : 'pointer'
                    }}
                    onClick={() => {
                      isDisabled
                        ? null
                        : navigate(
                            `/project/${cell.row.values.id}${
                              cell.row.values.project_status ===
                              ProjectStatus.INGESTED
                                ? '/review'
                                : '/edit'
                            }`
                          );
                    }}
                  >
                    <MDTypography
                      variant={'sm'}
                      ref={typographyRef}
                      color={isDisabled ? '' : 'info'}
                      sx={({ palette }: Theme) => ({
                        ...(isDisabled && { color: palette.grey[500] }),
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all'
                      })}
                    >
                      {cell.row.values.project_name}
                    </MDTypography>
                  </MDBox>
                </Tooltip>
              </Grid>
            </Grid>
          );
        }
      },
      {
        Header: () => (
          <Tooltip
            title={
              <div style={{ textAlign: 'left' }}>
                An uploaded file can have following status changes:
                <br />
                1. “Processing” when data is being read and recognised
                <br />
                2. “Processed” when data is successfully recognised and ready
                for next step i.e. Review
                <br />
                3. “Cleaning” when DataSquirrel is cleaning the imported data
                <br />
                4. “Ready” when data is cleaned, and a Project is created
              </div>
            }
            placement='right'
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 2,
                width: 'min-content'
              }}
            >
              <span>Status</span>
              <FaIcon
                icon='circle-question'
                style={{
                  verticalAlign: '0.125rem'
                }}
                typographyProps={{
                  lineHeight: 0.625,
                  color: 'info'
                }}
              />
            </div>
          </Tooltip>
        ),
        accessor: 'project_status',
        width: '25%',
        Cell: ({ cell }: any) => (
          <Grid
            container
            display='flex'
            direction='row'
            spacing={1}
            alignItems='center'
            flexWrap='nowrap'
          >
            <Grid item>
              {cell.row.values.project_status === ProjectStatus.PROCESSING ||
              cell.row.values.project_status === ProjectStatus.COMBINING ||
              cell.row.values.project_status === ProjectStatus.QUEUED ? (
                <MDBox
                  color={
                    getProjectStatus(cell.row.values.project_status)?.color
                  }
                  marginTop={0.5}
                >
                  <CircularProgress size={18} color='inherit' />
                </MDBox>
              ) : (
                <MDBox
                  variant='gradient'
                  color={
                    getProjectStatus(cell.row.values.project_status)?.color
                  }
                >
                  <FontAwesomeIcon icon='circle' />
                </MDBox>
              )}
            </Grid>
            <Tooltip
              title={
                getProjectStatus(cell.row.values.project_status)?.tooltipText ||
                ''
              }
              placement='top'
            >
              <Grid item>
                {getProjectStatus(cell.row.values.project_status)?.status || (
                  <></>
                )}
              </Grid>
            </Tooltip>
            <Grid item sx={{ textAlign: 'center' }}>
              {cell.row.values.project_status === ProjectStatus.INGESTED && (
                <Link
                  onClick={() =>
                    navigate(`/project/${cell.row.values.id}/review`)
                  }
                >
                  <MDTypography
                    pl={1}
                    variant='xs'
                    color='secondary'
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Click to Review
                  </MDTypography>
                </Link>
              )}
              {[ProjectStatus.PROCESSING, ProjectStatus.QUEUED].includes(
                cell.row.values.project_status
              ) && (
                <Tooltip
                  title='File processing for large files (3-10MB) can take up to 3 minutes. Please wait and avoid uploading the file again. Thank you!'
                  placement='top'
                >
                  <div>
                    <LinearProgressIndicator />
                  </div>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        )
      },
      {
        Header: '# of charts',
        accessor: 'chart_id_order',
        width: '5%',
        Cell: ({ cell }: any) => cell.row.values.chart_id_order?.length || 0
      },
      {
        Header: 'last updated',
        accessor: 'updated_at',
        width: '15%',
        Cell: ({ cell }: any) => dayjs(cell.row.values.updated_at).fromNow()
      },

      {
        Header: '',
        width: '5%',
        Cell: ({ cell }: any) => (
          <Tooltip title={MyProjectsPage.DELETE_PROJECT} placement='top'>
            <IconButton
              size='small'
              onClick={() =>
                handleDeleteIconClick(
                  cell.row.values.id,
                  cell.row.values.project_name,
                  cell.row.original?.chart_id_order?.length
                )
              }
              sx={({ palette }: Theme) => ({
                '&:hover': {
                  color: palette.dark.main
                }
              })}
            >
              <FaIcon
                icon='trash-can'
                typographyProps={{ color: 'inherit', variant: 'xl' }}
              />
            </IconButton>
          </Tooltip>
        ),
        accessor: 'hoverItem'
      }
    ],
    []
  );

  const renderTable = useMemo(
    () => (
      <DataTable
        table={{
          columns: MyProjectColumns,
          rows: data || []
        }}
        showTotalEntries={false}
        entriesPerPage={false}
      />
    ),
    [data]
  );

  const handleDeleteIconClick = (
    id: string,
    projectName: string,
    chartCount?: number
  ) => {
    setProjectId(id);
    setProjectName(projectName);
    setSelectedChartCount(chartCount || 0);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteProject = async () => {
    if (projectId) {
      await userDeleteProject(projectId);
      sendAmplitudeData(DELETE_PROJECT, { 'Total charts': selectedChartCount });
    }
    setIsDeleteModalOpen(false);
  };

  const handleNewProjectClicked = () => {
    sendAmplitudeData(NEW_PROJECT);
    sendGAData(NEW_PROJECT);
    setIsUploadModalOpen(true);
  };

  if (!data && isLoading) return <FullPageLoader />;

  return (
    <>
      <MainLayout
        pageTitle='My Projects'
        sectionTooltip='All projects (own and shared) along with their details are displayed in this page.'
        projectCount={data?.length}
      >
        <Grid container sx={{ mt: '1rem' }}>
          <MDTypography variant='h3' textGradient={true} color={Colors.INFO}>
            My Projects
          </MDTypography>
        </Grid>
        {isSuccess && data && data.length > 0 ? (
          <Grid container mt={2}>
            {renderTable}
          </Grid>
        ) : (
          <EmptyProjects setIsUploadModalOpen={handleNewProjectClicked} />
        )}
      </MainLayout>
      {isDeleteModalOpen && (
        <ConfirmationModal
          open={isDeleteModalOpen}
          onClose={handleDeleteModalClose}
          projectName={projectName}
          onProceedButtonClick={handleDeleteProject}
          title='Delete this project ?'
          subtitle='Are you sure you want to delete this project : '
          proceedButtonMessage='YES, DELETE!'
        />
      )}
      {isUploadModalOpen && (
        <FileUploadModal
          open={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          handleFileUploadSuccess={(isGoogleSheet: boolean) =>
            pushSuccessToast(
              <>
                We are processing your {isGoogleSheet ? 'Google Sheet' : 'file'}
                . Please check back the status of your file in{' '}
                <Link
                  href='/'
                  sx={{
                    borderBottom: '1px solid #000',
                    fontSize: '0.925rem',
                    fontFamily: 'Open Sans',
                    lineHeight: '1.6',
                    fontWeight: 500,
                    color: '#000'
                  }}
                >
                  My Projects.
                </Link>
              </>
            )
          }
        />
      )}
    </>
  );
};

export default MyProjects;
